import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { arc as arcBuilder } from "d3-shape"
import React from "react"

import styled from "styled-components"
import _sumBy from "lodash/sumBy"

import { d2r } from "../../utils/trig"

const OverviewWrap = styled.div<{ width: number }>`
  display: grid;
  grid-template-columns: ${({ width }) => width}px 1fr;
  padding: 1rem;
`

const ChartWrap = styled.div`
  position: relative;
  margin: 0 auto;
  padding: 0;
`

const TableWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 0 0 2rem;
`

const TableItem = styled.div<{ color: string }>`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;

  > label {
    position: relative;

    &::before {
      content: "";
      width: 2px;
      background: ${({ color }) => color};
      height: 100%;
      position: absolute;
      left: -10px;
      top: 0;
    }
  }
`

const CenterLabel = styled.div<{ color: string; sizeMultiplier: number }>`
  position: absolute;
  width: 100%;
  left: 0;
  text-align: center;
  top: 0;
  height: 100%;
  width: 100%;
  color: ${p => p.color};
  font-size: ${p => p.sizeMultiplier * 2}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-direction: column;
  justify-content: center;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > span {
    font-size: ${p => p.sizeMultiplier}rem;
    position: relative;
    left: 2px;
    top: ${p => p.sizeMultiplier * 0.4}rem;
    width: 10px;
  }
`

const PlotWrap = styled.div<{
  shadowThickness: number
  backgroundColor: string
}>`
  position: relative;
`

type TOverviewDatum = {
  additionalText?: string
  label: string
  labelSuffix?: string
  value: number
}

interface IOverviewProps {
  chartWidth?: number
  /**
   * The bars data
   * @default []
   */
  data: TOverviewDatum[]
  prefix?: string
  suffix?: string
  title: string
  value: number
  width: number
  target?: number
}

const checkValidPrefix = (str: string) =>
  !str || (str.length < 3 && !/\//.test(str))

function Overview({
  chartWidth = 200,
  data = [],
  suffix = "",
  prefix = "",
  title = ""
}: IOverviewProps) {
  const theme = useTheme()

  if (data.length > 4) {
    return "Data length should be <= 4"
  }

  const total = _sumBy(data, "value")
  const fracts = data.map(({ value }) => value / total)

  const innerColor = theme.basePalette.fg

  const lineThickness = 3
  const sizeMultiplier = 1
  const width = chartWidth

  const isValidPrefix = checkValidPrefix(prefix)
  const isValidSuffix = checkValidPrefix(suffix)

  if (!isValidPrefix) {
    return <p>Invalid Prefix `{prefix}`.</p>
  }
  if (!isValidSuffix) {
    return <p>Invalid Suffix `{suffix}`</p>
  }

  const arcPaths = []
  let lastEndAngle = 0
  for (let i = 0; i < fracts.length; i++) {
    const fr = fracts[i]
    const degrees = 360 * fr
    const arcPath = arcBuilder()({
      innerRadius: width * 0.45 - lineThickness * 0.5,
      outerRadius: width * 0.45 + lineThickness * 0.5,
      startAngle: d2r(lastEndAngle),
      endAngle: d2r(lastEndAngle + degrees),
      padAngle: 0.05
    })
    arcPaths.push(arcPath)
    lastEndAngle += degrees
  }

  return (
    <OverviewWrap width={width}>
      <ChartWrap style={{ width }}>
        <PlotWrap
          backgroundColor={theme.basePalette.bg}
          shadowThickness={width < 160 ? 5 : 10}
          style={{ width, height: width }}
        >
          <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
            <g transform={`translate(${width / 2}, ${width / 2})`}>
              <circle
                r={width * 0.38}
                cx={0}
                cy={0}
                stroke={`url(#inner-grad-primary)`}
                strokeOpacity={0.5}
                fill={"none"}
              />
              <circle
                r={width * 0.45}
                stroke={theme.colorScales.fg(0).alpha(0.5).css()}
                strokeDasharray={"1,2"}
                cx={0}
                cy={0}
                fill="none"
              />
              {arcPaths.map((path, i) => (
                <g key={`overview-path-${i}`}>
                  {path ? <path fill={theme.series[i]} d={path} /> : ""}
                </g>
              ))}
            </g>
            <defs>
              <linearGradient
                id={`inner-grad-primary`}
                gradientTransform="rotate(90)"
              >
                <stop offset={0} stopColor={innerColor} stopOpacity={0.1} />
                <stop offset={1} stopColor={innerColor} stopOpacity={0.2} />
              </linearGradient>
            </defs>
          </svg>
          <CenterLabel
            color={theme.palette.text.primary}
            sizeMultiplier={sizeMultiplier}
          >
            <div>
              {prefix && <span>{prefix}</span>}
              {total}
              {suffix && <span>{suffix}</span>}
            </div>
            {title ? <Typography>{title}</Typography> : ""}
          </CenterLabel>
        </PlotWrap>
      </ChartWrap>
      <TableWrap>
        {data.map((d, i) => (
          <TableItem key={`table-item-${i}`} color={theme.series[i]}>
            <label>{d.label}</label>
            <Typography>
              {d.value}{" "}
              <Typography variant="overline">
                {d.labelSuffix || ""}
                {d.additionalText ? (
                  <span style={{ opacity: 0.5 }}> | {d.additionalText}</span>
                ) : (
                  ""
                )}
              </Typography>
            </Typography>
          </TableItem>
        ))}
      </TableWrap>
    </OverviewWrap>
  )
}

export default Overview
